import axios from 'axios';
import { getMimicUser } from './mimic';
import { ACCESS_TOKEN_NAME, ACCESS_TOKEN_USER } from '../const';

const host = process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: host,
});

// Pick the most useful text the backend gave us. Most endpoints answer with
// `{ message }`, but the access guards (e.g. the unpaid-invoice leads block)
// answer with `{ error }`.
const extractMessage = (response) =>
  response.data?.message ||
  response.data?.error ||
  response.statusText ||
  `Request failed with status ${response.status}`;

// The backend's isAuth middleware answers with 400 "Invalid token" when the
// JWT fails verification (expired, tampered, rotated). That is the exact and
// unambiguous "your session is gone" signal — role / permission denials use
// 401 / 403 and must NOT force logout.
const isSessionLost = (response) =>
  response &&
  response.status === 400 &&
  response.data &&
  response.data.message === 'Invalid token';

// Guard against multiple concurrent 401-shaped responses each trying to
// redirect. First one wins; the rest just reject.
let sessionRedirectInFlight = false;
const forceSignOutAndRedirect = () => {
  if (sessionRedirectInFlight) return;
  sessionRedirectInFlight = true;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_NAME);
    window.localStorage.removeItem(ACCESS_TOKEN_USER);
  } catch (_) {
    /* localStorage unavailable in some contexts */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
    window.location.assign('/auth/sign-in');
  }
};

axiosInstance.interceptors.request.use((config) => {
  // Attach the current auth token on every outgoing request by reading it
  // straight from localStorage. Previously the token was pinned once onto
  // `axios.defaults.headers.common.Authorization` at login/boot, which meant
  // clearing localStorage (in another tab, or in DevTools) did NOT stop the
  // instance from sending the pinned Bearer — subsequent requests kept
  // succeeding. Reading per-request makes localStorage the single source of
  // truth so a real signout in any tab immediately stops working here.
  // Callers passing an explicit Authorization header (e.g. usersApi's
  // password-reset flows) are left untouched.
  const alreadyHasAuth = config.headers && config.headers.Authorization;
  if (!alreadyHasAuth) {
    let accessToken = null;
    try {
      accessToken =
        window.localStorage.getItem(ACCESS_TOKEN_USER) ||
        window.localStorage.getItem(ACCESS_TOKEN_NAME);
    } catch (_) {
      /* localStorage unavailable */
    }
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
  }

  if (config.skipMimic) return config;
  const mimic = getMimicUser();
  if (mimic && mimic.id && mimic.type) {
    config.headers = config.headers || {};
    config.headers['X-Mimic-User-Id'] = mimic.id;
    config.headers['X-Mimic-User-Type'] = mimic.type;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    if (response && response.status === 403) {
      console.warn('Permission denied:', response.data?.message || 'Access denied');
    }

    if (!response) {
      return Promise.reject(error);
    }

    if (isSessionLost(response)) {
      forceSignOutAndRedirect();
    }

    // This interceptor used to reject with the raw AxiosResponse. A response
    // object has no `message`, so every caller doing `err.message` (the auth
    // screens among them) fell back to a generic "Something went wrong" and the
    // real reason — "Customer is inactive", "No such email" — never reached the
    // user. Reject with a real Error carrying the backend message, while
    // keeping `data` / `status` / `response` reachable so the many callers
    // written against the old shape keep working.
    const normalizedError = new Error(extractMessage(response));
    normalizedError.data = response.data;
    normalizedError.status = response.status;
    normalizedError.statusText = response.statusText;
    normalizedError.headers = response.headers;
    normalizedError.config = response.config;
    normalizedError.response = response;

    return Promise.reject(normalizedError);
  },
);

export default axiosInstance;
